import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight, TrendingUp, Palmtree, FolderOpen, ClipboardCheck, BarChart3,
  History, Download, X, Search, Check, User, UserCog, Paperclip, ArrowRight,
} from "lucide-react";
import client, { API, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePerms } from "@/lib/perms";
import { EMTimeline, EMDocsPanel } from "@/components/EMProfileSections";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export const TrialBadge = () => null;

const TYPE_META = {
  transfer: { icon: ArrowLeftRight, color: "bg-sky-100 text-sky-700", key: "em_type_transfer" },
  supervisor_transfer: { icon: UserCog, color: "bg-indigo-100 text-indigo-700", key: "em_type_sup_transfer" },
  promotion: { icon: TrendingUp, color: "bg-violet-100 text-violet-700", key: "em_type_promotion" },
  vacation: { icon: Palmtree, color: "bg-emerald-100 text-emerald-700", key: "em_type_vacation" },
  vacation_return: { icon: Palmtree, color: "bg-teal-100 text-teal-700", key: "em_type_vacation_return" },
};
const STATUS_META = {
  pending: { color: "bg-amber-100 text-amber-700", key: "em_status_pending" },
  approved: { color: "bg-green-100 text-green-700", key: "em_status_approved" },
  rejected: { color: "bg-rose-100 text-rose-700", key: "em_status_rejected" },
};

const TypeBadge = ({ type, t }) => {
  const m = TYPE_META[type] || TYPE_META.transfer;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${m.color}`}><m.icon size={11} />{t(m.key)}</span>;
};
const StatusBadge = ({ status, t }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.color}`}>{t(m.key)}</span>;
};

function EmpPicker({ t, value, onChange, filter }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { client.get("/em/employees").then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  const s = q.trim().toLowerCase();
  const matches = s
    ? rows.filter((r) => (!filter || filter(r)) && (r.name.toLowerCase().includes(s) || r.new_id.includes(s))).slice(0, 6)
    : [];
  if (value) {
    return (
      <div className="flex items-center justify-between border border-sky-200 bg-sky-50 rounded-xl px-3 py-2 text-sm" data-testid="em-picked-employee">
        <span className="font-bold text-slate-700">{value.name} <span className="text-slate-400 text-xs">[{value.new_id}]</span></span>
        <button onClick={() => onChange(null)} className="p-0.5 rounded hover:bg-white"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search size={13} className="absolute top-3 start-2.5 text-slate-400" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("em_pick_employee")} className="w-full ps-8 pe-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="em-emp-search" />
      {matches.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {matches.map((r) => (
            <button key={r.id} onClick={() => { onChange(r); setQ(""); }} className="w-full text-start px-3 py-2 text-xs hover:bg-sky-50 flex justify-between" data-testid={`em-emp-opt-${r.id}`}>
              <span className="font-bold">{r.name}</span>
              <span className="text-slate-400">[{r.new_id}] {r.branch_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function vacDays(from, to) {
  const d1 = new Date(from), d2 = new Date(to);
  if (!from || !to || isNaN(d1) || isNaN(d2)) return null;
  const n = Math.round((d2 - d1) / 86400000);
  return n > 0 ? n : null;
}

export function vacDaysFromSummary(summary) {
  const m = String(summary || "").match(/(\d{4}-\d{2}-\d{2})\s*→\s*(\d{4}-\d{2}-\d{2})/);
  return m ? vacDays(m[1], m[2]) : null;
}

function RequestsTable({ items, t, onRowClick }) {
  return (
    <div className="bg-white rounded-2xl card-shadow overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
            {[t("em_col_type"), t("em_col_emp"), t("em_from"), t("em_to"), t("em_requested_by"), t("em_col_date"), t("em_col_status")].map((h) => (
              <th key={h} className="p-2.5 text-start font-bold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={7} className="p-8 text-center text-slate-400" data-testid="em-req-empty">{t("em_no_requests")}</td></tr>
          ) : items.map((r) => (
            <tr key={r.id} onClick={() => onRowClick && onRowClick(r)} className={`border-b border-slate-50 ${onRowClick ? "cursor-pointer hover:bg-sky-50/60" : ""}`} data-testid={`em-req-row-${r.id}`}>
              <td className="p-2.5"><TypeBadge type={r.type} t={t} /></td>
              <td className="p-2.5 font-bold text-slate-800 whitespace-nowrap">{r.employee_name} <span className="text-slate-400 font-normal">[{r.employee_code}]</span></td>
              <td className="p-2.5 whitespace-nowrap max-w-[200px] truncate" title={r.from_summary}>{r.from_summary}</td>
              <td className="p-2.5 whitespace-nowrap max-w-[280px] font-semibold" title={r.to_summary}>
                {r.type === "vacation" && vacDaysFromSummary(r.to_summary) ? (
                  <span className="inline-flex items-center gap-2">
                    <span dir="ltr">{r.to_summary}</span>
                    <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`em-req-days-${r.id}`}>
                      {t("em_days_count", { n: vacDaysFromSummary(r.to_summary) })}
                    </span>
                  </span>
                ) : (
                  <span className="block max-w-[240px] truncate">{r.to_summary}</span>
                )}
              </td>
              <td className="p-2.5 whitespace-nowrap">{r.requested_by_name}</td>
              <td className="p-2.5 whitespace-nowrap" style={{ direction: "ltr" }}>{(r.created_at || "").slice(0, 10)}</td>
              <td className="p-2.5"><StatusBadge status={r.status} t={t} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EMRequestsPage({ kind }) {
  const { t } = useT();
  const { user } = useAuth();
  const { can } = usePerms();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // {type}
  const [emp, setEmp] = useState(null);
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [peers, setPeers] = useState([]);
  const [sups, setSups] = useState([]);
  const [destAdmins, setDestAdmins] = useState([]);
  const [destBranches, setDestBranches] = useState([]);
  const [positions, setPositions] = useState([]);
  const [xlsxRange, setXlsxRange] = useState({ from: "", to: "" });

  const meta = {
    transfers: {
      types: ["transfer", "supervisor_transfer"], icon: ArrowLeftRight, title: "em_nav_transfers",
      actions: [
        { type: "transfer", label: "em_req_transfer_btn" },
        ...(user?.role !== "supervisor" ? [{ type: "supervisor_transfer", label: "em_req_sup_transfer_btn" }] : []),
      ],
    },
    promotions: { types: ["promotion"], icon: TrendingUp, title: "em_nav_promotions", actions: [{ type: "promotion", label: "em_req_promotion_btn" }] },
    vacations: { types: ["vacation", "vacation_return"], icon: Palmtree, title: "em_nav_vacations", actions: [{ type: "vacation", label: "em_req_vacation_btn" }, { type: "vacation_return", label: "em_req_return_btn" }] },
  }[kind];

  const load = async () => {
    try {
      const res = await client.get("/em/requests");
      setItems((res.data || []).filter((r) => meta.types.includes(r.type)));
    } catch { /* noop */ }
    if (kind === "vacations") {
      try {
        const r = await client.get("/em/employees");
        setOnVacation((r.data || []).filter((x) => x.on_vacation && !x.is_former));
      } catch { /* noop */ }
    }
  };
  const [onVacation, setOnVacation] = useState([]);
  const [ending, setEnding] = useState("");
  const endVacation = async (e) => {
    if (!window.confirm(t("em_end_vacation_confirm", { name: e.name }))) return;
    setEnding(e.id);
    try {
      await client.post(`/em/vacations/${e.id}/end`);
      toast.success(t("em_vacation_ended"));
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("em_request_error")); }
    finally { setEnding(""); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  useEffect(() => {
    if (modal?.type === "transfer" || modal?.type === "supervisor_transfer") client.get("/area-managers/peers").then((r) => setPeers(r.data || [])).catch(() => {});
    if (modal?.type === "supervisor_transfer") client.get("/admins").then((r) => setSups(r.data || [])).catch(() => {});
  }, [modal]);
  useEffect(() => {
    if (kind === "promotions") client.get("/positions").then((r) => setPositions(r.data || [])).catch(() => {});
  }, [kind]);

  useEffect(() => {
    const am = form.to_area_manager_id;
    if (modal?.type === "transfer" && am) {
      client.get(`/admins/by-area/${am}`).then((r) => setDestAdmins(r.data || [])).catch(() => setDestAdmins([]));
      client.get(`/branches/by-area/${am}`).then((r) => setDestBranches((r.data || []).filter((b) => !b.is_vacation && !b.is_admin))).catch(() => setDestBranches([]));
    }
  }, [form.to_area_manager_id, modal]);

  const send = async () => {
    const isSupT = modal.type === "supervisor_transfer";
    if (isSupT) {
      if (!form.admin_id || !form.to_area_manager_id) { toast.error(t("em_pick_supervisor")); return; }
    } else if (!emp) { toast.error(t("em_pick_employee")); return; }
    setSending(true);
    try {
      const payload = isSupT ? { type: modal.type, ...form } : { type: modal.type, employee_id: emp.id, ...form };
      const res = await client.post("/em/requests", payload);
      toast.success(res.data?.status === "approved" ? t("em_transfer_applied") : t("em_request_sent"));
      setModal(null); setEmp(null); setForm({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_request_error")); }
    finally { setSending(false); }
  };

  const inp = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm";
  const lbl = "text-[11px] font-bold text-slate-500 block mb-1";
  return (
    <div className="space-y-4" data-testid={`em-${kind}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
          <meta.icon className="text-sky-600" size={20} /> {t(meta.title)} <TrialBadge t={t} />
        </h1>
        <div className="flex gap-2 items-center flex-wrap">
          {kind === "transfers" && (
            <div className="flex items-center gap-1.5 bg-white border border-emerald-200 rounded-xl px-2 py-1" data-testid="em-xlsx-range">
              <input type="date" value={xlsxRange.from} onChange={(e) => setXlsxRange((s) => ({ ...s, from: e.target.value }))} className="text-[11px] border-0 bg-transparent focus:outline-none w-[110px]" title={t("em_xlsx_from")} data-testid="em-xlsx-from" />
              <span className="text-slate-300 text-xs">←</span>
              <input type="date" value={xlsxRange.to} onChange={(e) => setXlsxRange((s) => ({ ...s, to: e.target.value }))} className="text-[11px] border-0 bg-transparent focus:outline-none w-[110px]" title={t("em_xlsx_to")} data-testid="em-xlsx-to" />
              <button onClick={async () => {
                try {
                  const p = new URLSearchParams();
                  if (xlsxRange.from) p.set("date_from", xlsxRange.from);
                  if (xlsxRange.to) p.set("date_to", xlsxRange.to);
                  const res = await fetch(`${API}/em/transfers/xlsx?${p}`, { headers: { Authorization: `Bearer ${getToken()}` } });
                  const blob = await res.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  const m = /filename="(.+)"/.exec(res.headers.get("Content-Disposition") || "");
                  a.download = m ? m[1] : "STAFF TRANSFER FORM.xlsx";
                  a.click();
                  URL.revokeObjectURL(a.href);
                } catch { toast.error(t("em_download_error")); }
              }} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-xs font-bold rounded-lg px-3 py-1.5 shadow hover:opacity-90" data-testid="em-transfers-xlsx-btn">
                <Download size={13} /> {t("em_transfer_xlsx_btn")}
              </button>
            </div>
          )}
          {can("em_requests") && meta.actions.map((a) => (
            <button key={a.type} onClick={() => { setModal({ type: a.type }); setEmp(null); setForm({}); }}
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-sky-500 to-blue-600 text-white text-xs font-bold rounded-xl px-3.5 py-2 shadow hover:opacity-90"
              data-testid={`em-new-${a.type}-btn`}>
              + {t(a.label)}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-800 font-semibold">{t("em_approvals_flow_hint")}</div>
      {kind === "vacations" && (
        <div className="bg-white rounded-2xl card-shadow p-4" data-testid="em-on-vacation-card">
          <h2 className="font-black text-slate-800 text-sm flex items-center gap-2 mb-3">🌴 {t("em_on_vacation_now")} ({onVacation.length})</h2>
          {onVacation.length === 0 ? (
            <div className="text-xs text-slate-400" data-testid="em-no-vacation">{t("em_no_vacation_now")}</div>
          ) : (
            <div className="space-y-2">
              {onVacation.map((e) => {
                const overdue = e.vacation_return_expected && e.vacation_return_expected < new Date().toISOString().slice(0, 10);
                return (
                <div key={e.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 border ${overdue ? "bg-rose-50 border-rose-300" : "bg-emerald-50/50 border-emerald-100"}`} data-testid={`em-vacation-row-${e.id}`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-bold ${overdue ? "text-rose-700" : "text-slate-800"} flex items-center gap-2`}>
                      {e.name}
                      {overdue && <span className="text-[10px] font-black bg-rose-600 text-white px-2 py-0.5 rounded-full" data-testid={`em-vacation-overdue-${e.id}`}>🔴 {t("em_vacation_overdue")}</span>}
                    </div>
                    <div className={`flex items-center flex-wrap gap-1.5 mt-0.5 text-[11px] ${overdue ? "text-rose-500 font-bold" : "text-slate-500"}`}>
                      <span>{e.mum || "—"}</span>
                      <span>·</span>
                      <span dir="ltr">{e.vacation_start || "؟"} → {e.vacation_return_expected || "؟"}</span>
                      {vacDays(e.vacation_start, e.vacation_return_expected) && (
                        <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`em-vac-duration-${e.id}`}>
                          {t("em_vac_duration", { n: vacDays(e.vacation_start, e.vacation_return_expected) })}
                        </span>
                      )}
                      {!overdue && vacDays(new Date().toISOString().slice(0, 10), e.vacation_return_expected) && (
                        <span className="text-[10px] font-black bg-sky-100 text-sky-700 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`em-vac-remaining-${e.id}`}>
                          {t("em_vac_remaining", { n: vacDays(new Date().toISOString().slice(0, 10), e.vacation_return_expected) })}
                        </span>
                      )}
                    </div>
                  </div>
                  {can("em_requests") && (
                    <button onClick={() => endVacation(e)} disabled={ending === e.id}
                      className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-[11px] font-bold rounded-xl px-3 py-1.5 shadow hover:opacity-90 disabled:opacity-50"
                      data-testid={`em-end-vacation-btn-${e.id}`}>
                      ✅ {t("em_end_vacation_btn")}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <RequestsTable items={items} t={t} />

      {modal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="em-request-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm flex items-center gap-2"><TypeBadge type={modal.type} t={t} /></h3>
              <button onClick={() => setModal(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="em-request-close"><X size={16} /></button>
            </div>
            {modal.type !== "supervisor_transfer" && (
              <div>
                <label className={lbl}>{t("em_col_emp")}</label>
                <EmpPicker t={t} value={emp} onChange={setEmp} filter={modal.type === "vacation_return" ? undefined : undefined} />
              </div>
            )}
            {modal.type === "supervisor_transfer" && (
              <>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-2.5 text-[11px] text-indigo-800 font-semibold" data-testid="em-sup-transfer-hint">
                  {t("em_sup_transfer_hint")}
                </div>
                <div>
                  <label className={lbl}>{t("em_pick_supervisor")}</label>
                  <select className={inp} value={form.admin_id || ""} onChange={(e) => setForm((s) => ({ ...s, admin_id: e.target.value }))} data-testid="em-sup-select">
                    <option value="">—</option>
                    {sups.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>{t("em_pick_dest_am")}</label>
                  <select className={inp} value={form.to_area_manager_id || ""} onChange={(e) => setForm((s) => ({ ...s, to_area_manager_id: e.target.value }))} data-testid="em-sup-dest-am">
                    <option value="">—</option>
                    {peers.map((p) => <option key={p.id} value={p.id}>{p.full_name || p.username}</option>)}
                  </select>
                </div>
              </>
            )}
            {modal.type === "transfer" && (
              <>
                <div>
                  <label className={lbl}>{t("em_pick_dest_am")}</label>
                  <select className={inp} value={form.to_area_manager_id || ""} onChange={(e) => setForm((s) => ({ ...s, to_area_manager_id: e.target.value, to_admin_id: "", to_branch_id: "" }))} data-testid="em-dest-am">
                    <option value="">—</option>
                    {peers.map((p) => <option key={p.id} value={p.id}>{p.full_name || p.username}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>{t("em_pick_dest_mum")}</label>
                  <select className={inp} value={form.to_admin_id || ""} onChange={(e) => setForm((s) => ({ ...s, to_admin_id: e.target.value }))} data-testid="em-dest-mum">
                    <option value="">—</option>
                    {destAdmins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>{t("em_pick_dest_branch")}</label>
                  <select className={inp} value={form.to_branch_id || ""} onChange={(e) => setForm((s) => ({ ...s, to_branch_id: e.target.value }))} data-testid="em-dest-branch">
                    <option value="">—</option>
                    {destBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </>
            )}
            {modal.type === "promotion" && (
              <div>
                <label className={lbl}>{t("em_new_position")}</label>
                <select className={inp} value={form.new_position || ""} onChange={(e) => setForm((s) => ({ ...s, new_position: e.target.value }))} data-testid="em-new-position">
                  <option value="">—</option>
                  {positions.map((p) => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            )}
            {modal.type === "vacation" && (
              <>
                <div><label className={lbl}>{t("em_start_date")}</label>
                  <input type="date" className={inp} style={{ direction: "ltr" }} value={form.start_date || ""} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} data-testid="em-vac-start" /></div>
                <div><label className={lbl}>{t("em_return_date")}</label>
                  <input type="date" className={inp} style={{ direction: "ltr" }} value={form.return_date || ""} onChange={(e) => setForm((s) => ({ ...s, return_date: e.target.value }))} data-testid="em-vac-return" /></div>
                {form.start_date && form.return_date && (
                  vacDays(form.start_date, form.return_date) ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="em-vac-days-badge">
                      <span className="text-lg">🌴</span>
                      <span className="text-sm font-black text-emerald-700">
                        {t("em_days_selected", { n: vacDays(form.start_date, form.return_date) })}
                      </span>
                    </div>
                  ) : (
                    <div className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="em-vac-days-invalid">
                      {t("em_days_invalid")}
                    </div>
                  )
                )}
              </>
            )}
            {modal.type === "vacation_return" && (
              <div><label className={lbl}>{t("em_actual_return")}</label>
                <input type="date" className={inp} style={{ direction: "ltr" }} value={form.actual_return_date || ""} onChange={(e) => setForm((s) => ({ ...s, actual_return_date: e.target.value }))} data-testid="em-vac-actual" /></div>
            )}
            <div><label className={lbl}>{t("em_note_label")}</label>
              <input className={inp} value={form.note || ""} onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))} data-testid="em-req-note" /></div>
            <button onClick={send} disabled={sending} className="w-full bg-gradient-to-br from-sky-500 to-blue-600 text-white font-bold text-sm rounded-xl py-2.5 shadow hover:opacity-90 disabled:opacity-50" data-testid="em-send-request">
              {sending ? t("loading") : t("em_send_request")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function EMApprovals() {
  const { t } = useT();
  const { user } = useAuth();
  const { can } = usePerms();
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(sp.get("tab") === "history" ? "history" : "pending");
  const [statusFilter, setStatusFilter] = useState(sp.get("status") || "");
  const [rejecting, setRejecting] = useState(null); // request id
  const [reason, setReason] = useState("");
  const [approving, setApproving] = useState(null); // request id
  const [approveNote, setApproveNote] = useState("");
  const [busy, setBusy] = useState(false);
  const canDecide = user?.role !== "supervisor" && can("em_approve");

  const load = async () => {
    try { setItems((await client.get("/em/requests")).data || []); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const todayIso = new Date().toISOString().slice(0, 10);
  const shown = items.filter((r) => {
    if (tab === "pending") return r.status === "pending";
    if (r.status === "pending") return false;
    if (statusFilter === "approved_today") return r.status === "approved" && (r.decided_at || "").slice(0, 10) === todayIso;
    if (statusFilter === "rejected") return r.status === "rejected";
    return true;
  });

  const decide = async (rid, approve) => {
    setBusy(true);
    try {
      if (approve) await client.post(`/em/requests/${rid}/approve`, { note: approveNote.trim() });
      else await client.post(`/em/requests/${rid}/reject`, { reason });
      toast.success(approve ? t("em_approved_toast") : t("em_rejected_toast"));
      setRejecting(null); setReason(""); setApproving(null); setApproveNote("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_request_error")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="em-approvals-page">
      <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
        <ClipboardCheck className="text-sky-600" size={20} /> {t("em_nav_approvals")} <TrialBadge t={t} />
        <span className="text-xs font-bold bg-amber-200 text-amber-800 rounded-full px-2.5 py-1" data-testid="em-pending-count">{items.filter((r) => r.status === "pending").length}</span>
      </h1>
      {!canDecide && <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 font-semibold">{t("em_approvals_only_am")}</div>}
      <div className="flex gap-2 flex-wrap items-center">
        {["pending", "history"].map((k) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-xl text-xs font-bold ${tab === k ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`em-approvals-tab-${k}`}>
            {t(k === "pending" ? "em_tab_pending" : "em_tab_history")}
          </button>
        ))}
        {tab === "history" && (
          <div className="flex gap-1.5 items-center ms-2" data-testid="em-history-filters">
            <span className="text-[10px] font-bold text-slate-400">{t("em_filter_label")}:</span>
            {[["", "em_filter_all"], ["approved_today", "em_filter_approved_today"], ["rejected", "em_filter_rejected"]].map(([v, lk]) => (
              <button key={v || "all"} onClick={() => setStatusFilter(v)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition ${statusFilter === v ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`em-history-filter-${v || "all"}`}>
                {t(lk)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {shown.length === 0 && <div className="bg-white rounded-2xl card-shadow p-8 text-center text-slate-400 text-sm" data-testid="em-approvals-empty">{t("em_no_requests")}</div>}
        {shown.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl card-shadow p-4" data-testid={`em-approval-card-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge type={r.type} t={t} />
                <span className="font-black text-slate-800 text-sm">{r.employee_name}</span>
                <span className="text-slate-400 text-xs">[{r.employee_code}]</span>
                <StatusBadge status={r.status} t={t} />
              </div>
              <span className="text-[11px] text-slate-400" style={{ direction: "ltr" }}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 rounded-xl p-2.5"><span className="font-bold text-slate-500">{t("em_from")}:</span> {r.from_summary || "—"}</div>
              <div className="bg-sky-50 rounded-xl p-2.5">
                <span className="font-bold text-sky-700">{t("em_to")}:</span>{" "}
                {r.type === "vacation" && vacDaysFromSummary(r.to_summary) ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span dir="ltr">{r.to_summary}</span>
                    <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 whitespace-nowrap">
                      {t("em_days_count", { n: vacDaysFromSummary(r.to_summary) })}
                    </span>
                  </span>
                ) : (r.to_summary || "—")}
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              {t("em_requested_by")}: <b>{r.requested_by_name}</b>
              {r.note && <> · {t("em_note_label")}: {r.note}</>}
              {r.status !== "pending" && r.decided_by_name && <> · {t("em_decided_by")}: <b>{r.decided_by_name}</b></>}
              {r.status === "rejected" && r.reject_reason && <> · <span className="text-rose-600">{t("em_reject_reason")}: {r.reject_reason}</span></>}
            </div>
            {r.status === "pending" && canDecide && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {approving === r.id ? (
                  <>
                    <input value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder={t("em_approve_note_ph")} className="border border-green-200 rounded-xl px-3 py-2 text-xs w-56" data-testid={`em-approve-note-${r.id}`} />
                    <button onClick={() => decide(r.id, true)} disabled={busy} className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-bold rounded-xl px-3.5 py-2 hover:bg-green-700 disabled:opacity-50" data-testid={`em-approve-confirm-${r.id}`}>
                      <Check size={13} /> {t("em_approve_confirm")}
                    </button>
                    <button onClick={() => { setApproving(null); setApproveNote(""); }} className="text-xs text-slate-400 font-bold px-2">{t("cancel")}</button>
                  </>
                ) : (
                  <button onClick={() => { setApproving(r.id); setRejecting(null); }} disabled={busy} className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-bold rounded-xl px-3.5 py-2 hover:bg-green-700 disabled:opacity-50" data-testid={`em-approve-${r.id}`}>
                    <Check size={13} /> {t("em_approve")}
                  </button>
                )}
                {rejecting === r.id ? (
                  <>
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("em_reject_reason_ph")} className="border border-rose-200 rounded-xl px-3 py-2 text-xs w-56" data-testid={`em-reject-reason-${r.id}`} />
                    <button onClick={() => decide(r.id, false)} disabled={busy || !reason.trim()} className="bg-rose-600 text-white text-xs font-bold rounded-xl px-3.5 py-2 hover:bg-rose-700 disabled:opacity-50" data-testid={`em-reject-confirm-${r.id}`}>{t("em_reject")}</button>
                    <button onClick={() => { setRejecting(null); setReason(""); }} className="text-xs text-slate-400 font-bold px-2">{t("cancel")}</button>
                  </>
                ) : (
                  <button onClick={() => { setRejecting(r.id); setApproving(null); }} className="inline-flex items-center gap-1 bg-white border border-rose-300 text-rose-600 text-xs font-bold rounded-xl px-3.5 py-2 hover:bg-rose-50" data-testid={`em-reject-${r.id}`}>
                    <X size={13} /> {t("em_reject")}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function EMArchive() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [fType, setFType] = useState("");
  useEffect(() => { client.get("/em/history").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  const shown = fType ? items.filter((h) => h.type === fType) : items;
  const download = async () => {
    try {
      const res = await fetch(`${API}/em/history/xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "Movements Archive.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(t("em_download_error")); }
  };
  return (
    <div className="space-y-4" data-testid="em-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
          <BarChart3 className="text-sky-600" size={20} /> {t("em_archive_title")} <TrialBadge t={t} />
        </h1>
        <div className="flex gap-2 items-center">
          <select value={fType} onChange={(e) => setFType(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="em-archive-type">
            <option value="">{t("em_all_types")}</option>
            {Object.keys(TYPE_META).map((k) => <option key={k} value={k}>{t(TYPE_META[k].key)}</option>)}
          </select>
          <button onClick={download} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-xs font-bold rounded-xl px-3.5 py-2 shadow hover:opacity-90" data-testid="em-archive-download">
            <Download size={14} /> {t("em_archive_download")}
          </button>
        </div>
      </div>
      <div className="bg-white rounded-2xl card-shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
              {[t("em_col_date"), t("em_col_type"), t("em_col_emp"), t("em_from"), t("em_to"), t("em_requested_by"), t("em_decided_by")].map((h) => (
                <th key={h} className="p-2.5 text-start font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400" data-testid="em-archive-empty">{t("em_no_requests")}</td></tr>
            ) : shown.map((h) => (
              <tr key={h.id} className="border-b border-slate-50" data-testid={`em-archive-row-${h.id}`}>
                <td className="p-2.5 whitespace-nowrap" style={{ direction: "ltr" }}>{(h.applied_at || "").slice(0, 10)}</td>
                <td className="p-2.5"><TypeBadge type={h.type} t={t} /></td>
                <td className="p-2.5 font-bold whitespace-nowrap">{h.employee_name} <span className="text-slate-400 font-normal">[{h.employee_code}]</span></td>
                <td className="p-2.5 max-w-[200px] truncate" title={h.from_summary}>{h.from_summary}</td>
                <td className="p-2.5 max-w-[200px] truncate font-semibold" title={h.to_summary}>{h.to_summary}</td>
                <td className="p-2.5 whitespace-nowrap">{h.requested_by_name}</td>
                <td className="p-2.5 whitespace-nowrap">{h.approved_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EMEmployeeArchive() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const canRestore = user?.role === "super_admin" || user?.role === "area_manager";
  const load = () => {
    setLoading(true);
    client.get("/em/employees?include_former=1")
      .then((r) => setRows((r.data || []).filter((x) => x.is_former)))
      .catch(() => toast.error(t("em_load_error")))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (r) => {
    if (!window.confirm(t("em_restore_confirm", { name: r.name }))) return;
    try {
      await client.post(`/employees/${r.id}/restore`);
      toast.success(t("em_restored_ok"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };
  const s = q.trim().toLowerCase();
  const shown = s ? rows.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.new_id || "").includes(s) || (r.iqama || "").includes(s)) : rows;
  return (
    <div className="space-y-4" data-testid="em-former-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
          🗄️ {t("em_emp_archive_title")} <TrialBadge t={t} />
          <span className="text-xs font-bold bg-rose-100 text-rose-700 rounded-full px-2.5 py-1" data-testid="em-archive-count">{rows.length}</span>
        </h1>
        <div className="relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("em_search_ph")}
            className="border border-slate-200 rounded-xl ps-9 pe-3 py-2 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="em-archive-search" />
        </div>
      </div>
      <p className="text-[11px] text-slate-400">{t("em_former_hint")}</p>
      <div className="bg-white rounded-2xl card-shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-rose-50/60 border-b border-slate-200 text-slate-600">
              {["SN", t("em_col_name"), "NEW ID", t("em_last_branch"), t("em_col_position"), t("em_last_sup"), t("em_archived_on"), t("em_archived_reason"), t("em_archived_by"), t("em_restore")].map((h) => (
                <th key={h} className="p-2.5 text-start font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="p-8 text-center text-slate-400">{t("loading")}</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={10} className="p-8 text-center text-slate-400" data-testid="em-former-empty">{t("em_former_empty")}</td></tr>
            ) : shown.map((r, i) => {
              const left = typeof r.restore_days_left === "number" ? r.restore_days_left : 0;
              return (
                <tr key={r.id} onClick={() => navigate(`/employee-management/employees/${r.id}`)} className="border-b border-slate-50 cursor-pointer bg-rose-50/30 hover:bg-rose-50" data-testid={`em-former-row-${r.id}`}>
                  <td className="p-2.5 text-slate-400">{i + 1}</td>
                  <td className="p-2.5 font-bold text-slate-800 whitespace-nowrap">{r.name} <span className="ms-1 text-[9px] font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full" data-testid={`em-former-tag-${r.id}`}>{t("em_former_badge")}</span></td>
                  <td className="p-2.5 font-bold" style={{ direction: "ltr" }}>{r.new_id}</td>
                  <td className="p-2.5 whitespace-nowrap">{r.branch_name}</td>
                  <td className="p-2.5 whitespace-nowrap">{r.position}</td>
                  <td className="p-2.5 whitespace-nowrap">{r.mum}</td>
                  <td className="p-2.5 whitespace-nowrap" style={{ direction: "ltr" }}>{(r.deleted_at || "").slice(0, 10)}</td>
                  <td className="p-2.5 max-w-[160px] truncate" title={r.deleted_reason}>{r.deleted_reason || "—"}</td>
                  <td className="p-2.5 whitespace-nowrap">{r.deleted_by}</td>
                  <td className="p-2.5 whitespace-nowrap">
                    {left > 0 ? (
                      <>
                        {canRestore && (
                          <button onClick={(e) => { e.stopPropagation(); restore(r); }} className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-200" data-testid={`em-restore-btn-${r.id}`}>↩ {t("em_restore")}</button>
                        )}
                        <span className="ms-1.5 text-[10px] font-bold text-slate-400">{t("em_restore_left", { n: left })}</span>
                      </>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400" data-testid={`em-restore-expired-${r.id}`}>{t("em_restore_expired")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EMAudit() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/em/requests").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="space-y-4" data-testid="em-audit-page">
      <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
        <History className="text-sky-600" size={20} /> {t("em_nav_audit")} <TrialBadge t={t} />
      </h1>
      <RequestsTable items={items} t={t} />
    </div>
  );
}

export function EMFiles() {
  const { t } = useT();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { client.get("/em/employees?include_former=1").then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  const s = q.trim().toLowerCase();
  const shown = s ? rows.filter((r) => r.name.toLowerCase().includes(s) || r.new_id.includes(s)).slice(0, 30) : rows.slice(0, 30);
  return (
    <div className="space-y-4" data-testid="em-files-page">
      <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
        <FolderOpen className="text-sky-600" size={20} /> {t("em_nav_files")} <TrialBadge t={t} />
      </h1>
      <p className="text-xs text-slate-400 -mt-2">{t("em_files_hint")}</p>
      <div className="relative max-w-sm">
        <Search size={13} className="absolute top-3 start-2.5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("em_pick_employee")} className="w-full ps-8 pe-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="em-files-search" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((r) => (
          <button key={r.id} onClick={() => navigate(`/employee-management/employees/${r.id}`)} className="bg-white rounded-2xl card-shadow p-4 flex items-center gap-3 text-start hover:border-sky-300 border border-transparent" data-testid={`em-files-emp-${r.id}`}>
            <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center shrink-0"><Paperclip size={18} /></div>
            <div className="min-w-0">
              <div className="font-bold text-slate-800 text-sm truncate">{r.name} {r.is_former && <span className="text-[9px] font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full">{t("em_former_badge")}</span>}</div>
              <div className="text-[11px] text-slate-400">[{r.new_id}] {r.branch_name}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function EMProfile() {
  const { t } = useT();
  const { empId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    client.get(`/em/employees/${empId}/profile`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [empId]);
  if (err) return <div className="bg-white rounded-2xl card-shadow p-8 text-center text-slate-400">{t("em_load_error")}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400">{t("loading")}</div>;
  const e = data.employee;
  const Field = ({ label, value }) => (
    <div className="bg-slate-50 rounded-xl p-2.5">
      <div className="text-[10px] font-bold text-slate-400">{label}</div>
      <div className="text-sm font-bold text-slate-800 truncate">{value || "—"}</div>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="em-profile-page">
      <button onClick={() => navigate("/employee-management/employees")} className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:underline" data-testid="em-profile-back">
        <ArrowRight size={13} /> {t("em_nav_employees")}
      </button>
      <div className="bg-white rounded-2xl card-shadow p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center"><User size={26} /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-black text-slate-800 flex items-center gap-2 flex-wrap">
              {e.name} <span className="text-slate-400 text-sm font-bold">[{e.new_id}]</span>
              {e.on_vacation && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{t("em_on_vacation_badge")}</span>}
              {e.is_former && <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full" data-testid="em-former-badge">{t("em_former_badge")}</span>}
              <TrialBadge t={t} />
            </h1>
            <div className="text-xs text-slate-500 mt-0.5">{e.position || "—"} · {e.branch || "—"} · {e.mum || "—"} · {e.am || "—"}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <Field label="OLD ID" value={e.old_id} />
          <Field label={t("emp_hire_date")} value={e.hire_date} />
          <Field label="COMPANY" value={e.company} />
          <Field label="NATIONALITY" value={e.nationality} />
          <Field label="IQAMA" value={e.iqama} />
          <Field label={t("phone")} value={e.phone} />
          <Field label={t("em_profile_cert")} value={e.health_expiry_g} />
          <Field label="NOTE" value={e.em_note} />
        </div>
        {e.on_vacation && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 font-semibold" data-testid="em-profile-vacation">
            🌴 {t("em_start_date")}: {e.vacation_start || "—"} · {t("em_return_date")}: {e.vacation_return_expected || "—"}
          </div>
        )}
      </div>

      {data.pending.length > 0 && (
        <div>
          <h2 className="text-sm font-black text-slate-700 mb-2">{t("em_profile_pending")} ({data.pending.length})</h2>
          <RequestsTable items={data.pending} t={t} />
        </div>
      )}

      <EMTimeline empId={e.id} />

      <div>
        <h2 className="text-sm font-black text-slate-700 mb-2">{t("em_docs_title")}</h2>
        <div className="bg-white rounded-2xl card-shadow p-5">
          <EMDocsPanel empId={e.id} />
        </div>
      </div>
    </div>
  );
}
